import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  HealthSystemTableRequestData,
  HealthSystemUser,
  HealthSystemUsersFilters,
  HealthSystemUsersFiltersFunc,
  HealthSystemUsersParams
} from '../../ShortView/Users/types'
import { DEFAULT_PAGE } from '../../../../../components/Table/constants'
import axios, { CancelTokenSource } from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from '../../../../../redux/store/user/getters'
import { Table } from '../../../../../components/Table'
import {
  fullFormatPhoneNumber,
  isTableHasParams
} from '../../../../../helper/common'
import { BTN_TXT, PLACEHOLDERS, USER_STATUSES } from '../../../../../constants'
import { setLoading } from '../../../../../redux/store/common/slice'
import { getHealthSystemUsers } from '../../ShortView/Users/api'
import { useDebounce } from '../../../../../hooks/hooks'
import { ability, ACTIONS, ROLES, SUBJECTS } from '../../../../Permission'
import { useParams } from 'react-router-dom'
import { routes } from '../../../../../router'
import useRouter from '../../../../../hooks/useRouter'
import { ROLES_LIST, columns } from './constants'
import { ReactComponent as Close16 } from '../../../../../assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from '../../../../../assets/svg/Search16.svg'
import { Input } from '../../../../../components/Input'
import { onStartChat } from '../../../utils'
import { Button, BUTTON_TYPES } from '../../../../../components/Button'
import { ReactComponent as Plus16 } from '../../../../../assets/svg/Plus16.svg'
import { ReactComponent as Edit16 } from '../../../../../assets/svg/Edit16.svg'
import { UserArchivePopup } from '../../../../../pages/CRUDUser/UserArchivePopup'
import {
  Typography,
  TYPOGRAPHY_WEIGHT
} from '../../../../../components/Typography'
import defineAbilityFor from '../../../../Permission/ability'
import { UserRolesFilterComponent } from '../../../../../components/UserRolesFilterComponent/UserRolesFilterComponent'
import { Tooltip } from 'components/Tooltip/Tooltip'

export const Users = ({
  outsideCommunityMode
}: {
  outsideCommunityMode?: boolean
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [selectedByActionsUser, setSelectedByActionsUser] =
    useState<HealthSystemUser | null>(null)
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [tableData, setTableData] = useState<HealthSystemUser[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [sortParams, setSortParams] = useState<HealthSystemTableRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [filters, setFilters] = useState<HealthSystemUsersFilters>({})
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [selectedRows, setSelectedRows] = useState<HealthSystemUser[]>([])
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const user = useSelector(getUser)

  const { id: hsId } = useParams()
  const effectiveHsId = outsideCommunityMode ? user.health_system : hsId

  const dispatch = useDispatch()
  const { push } = useRouter()

  const canEditUser = useMemo(() => {
    if (!activeTab) return true
    const role =
      activeTab === ROLES_LIST[0].value ? ROLES.VICE_PRESIDENT : activeTab
    return (
      ability.can(ACTIONS.CRUD, SUBJECTS.HEALTH_SYSTEM_USERS) &&
      !defineAbilityFor(role, user).can(ACTIONS.EDIT, SUBJECTS.FORM_ITEM) &&
      (user.role === ROLES.COMMUNITY_PRESIDENT ||
        effectiveHsId === user.health_system)
    )
  }, [effectiveHsId, activeTab, user])

  const canCrudUsersInHSDetails = ability.can(
    ACTIONS.CRUD,
    SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS
  )

  const debouncedSearch = useDebounce(searchValue, 500)

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => getTableData({ ...sortParams, ...filters }),
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeTab])

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const applyFilters: HealthSystemUsersFiltersFunc = (
    field,
    appliedFilters
  ) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: HealthSystemTableRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: HealthSystemUsersParams = { ...filters }
    if (searchValue) {
      params.search = searchValue
    }

    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getHealthSystemUsers(effectiveHsId, activeTab, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            name: i.first_name,
            phone_number: fullFormatPhoneNumber(i.phone_number),
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const handleSelectRow = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRows)
  }

  const onChangeRole = (tab: string) => {
    setSelectedRowKeys([])
    setSelectedRows([])
    setFilters({})
    setSortParams({})
    setSearchValue('')
    setActiveTab(tab)
  }

  const createUser = () =>
    push(
      `${routes.createUserProfile}/new?hsUser=${effectiveHsId}&fromCommunity=true`,
      {
        state: { selectedHs: effectiveHsId }
      }
    )

  const onArchive = useCallback((user) => {
    setIsArchiveOpen(true)
    setSelectedByActionsUser(user)
  }, [])

  const onEditUser = useCallback(() => {
    push(`${routes.createUserProfile}/${selectedRowKeys[0]}?fromCommunity=true`)
  }, [push, selectedRowKeys])

  const onCancelSelection = useCallback(() => {
    setIsArchiveOpen(false)
    setSelectedByActionsUser(null)
    setSelectedRowKeys([])
    setSelectedRows([])
  }, [])

  const tableColumns = useMemo(() => {
    return columns({
      filters,
      applyFilters,
      selectedRole: activeTab,
      hsId: effectiveHsId ?? '',
      onStartChat,
      onArchive,
      canEditUser,
      canCrudUsersInHSDetails
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    filters,
    effectiveHsId,
    onArchive,
    canEditUser,
    canCrudUsersInHSDetails
  ])

  return (
    <div className="column gap-16 full-width">
      <div className="column gap-4">
        <div className="row space-between">
          <div className="row gap-8 align-center">
            <UserRolesFilterComponent
              roles={ROLES_LIST}
              onChange={onChangeRole}
            />
            {!!selectedRowKeys.length ? (
              <Typography.Body1
                className="table-wrapper__select-title"
                weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
              >
                Selected {selectedRowKeys.length} of {totalNumber}
              </Typography.Body1>
            ) : (
              <Input
                className="vendor-users-list__search allow-clear"
                propsInput={{
                  allowClear: { clearIcon: <Close16 /> },
                  placeholder: PLACEHOLDERS.SEARCH_BY_NAME_AND_EMAIL,
                  prefix: <Search16 />,
                  value: searchValue,
                  onChange: (e) => setSearchValue(e.target.value)
                }}
              />
            )}
          </div>
          <div className="row gap-8">
            {canCrudUsersInHSDetails && !selectedRowKeys.length && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Plus16 />}
                onClick={createUser}
                upperCase
              >
                {BTN_TXT.CREATE_USER}
              </Button>
            )}
            {selectedRowKeys.length === 1 && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Edit16 />}
                upperCase
                onClick={onEditUser}
              >
                {BTN_TXT.EDIT}
              </Button>
            )}
            {((!!selectedRows.length &&
              selectedRows.filter((i) => i.status !== USER_STATUSES.ARCHIVED)
                .length === selectedRows.length) ||
              isArchiveOpen) && (
              <UserArchivePopup<HealthSystemUser>
                selected={
                  isArchiveOpen && selectedByActionsUser
                    ? [selectedByActionsUser]
                    : selectedRows
                }
                callback={() => {
                  onCancelSelection()
                  getTableData({ ...sortParams, ...filters })
                }}
                isTable
                isOpenFromTheOutside={isArchiveOpen}
                setIsOpenFromTheOutside={setIsArchiveOpen}
              />
            )}
          </div>
        </div>
        <Table<HealthSystemUser>
          dataSource={!tableColumns.length ? [] : tableData}
          columns={tableColumns}
          emptyText="No users found"
          className="community-users-table"
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          hasSearchOrFilters={isTableHasParams<HealthSystemUsersFilters>(
            debouncedSearch,
            filters
          )}
          createButton={
            canEditUser && canCrudUsersInHSDetails
              ? {
                  buttonText: BTN_TXT.CREATE_USER,
                  availability: true,
                  action: createUser
                }
              : undefined
          }
          rowSelection={
            canEditUser && canCrudUsersInHSDetails
              ? {
                  selectedRowKeys,
                  onChange: handleSelectRow,
                  getCheckboxProps: (record) => ({
                    disabled:
                      record.uuid === user.uuid ||
                      ('can_be_managed' in record && !record.can_be_managed)
                  }),
                  renderCell: (_checked, record, _, node) => {
                    if ('can_be_managed' in record && !record.can_be_managed) {
                      return (
                        <Tooltip
                          title={
                            'You do not have permission to manage users of this role'
                          }
                          overlayInnerStyle={{
                            display: 'block',
                            width: 264
                          }}
                          placement="topLeft"
                        >
                          {node}
                        </Tooltip>
                      )
                    }
                    return node
                  }
                }
              : undefined
          }
        />
      </div>
    </div>
  )
}
